import React, { useState, useEffect } from 'react';
import { FiSearch, FiEye } from 'react-icons/fi';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import Input from '../components/Input';
import { apiClient } from '../utils/api';

const Transactions = ({ user, onLogout }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(10);
  const [search, setSearch] = useState('');
  const [email, setEmail] = useState('');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const fetchTransactions = async (page = 1, searchTerm = '', emailFilter = '') => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.getTransactions(page, limit, searchTerm, emailFilter);
      
      if (response.data && response.data.data) {
        setTransactions(response.data.data);
        console.log(response.data.data);
        setTotal(response.data.total);
        setTotalPages(response.data.totalPages || Math.ceil(response.data.total / limit));
        setCurrentPage(response.data.page);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch transactions');
      console.error('Error fetching transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactionDetails = async (id) => {
    setModalLoading(true);
    try {
      const response = await apiClient.get(`transactions/${id}`);
      console.log(response.data);
      if (response.data && response.data) {
        setSelectedTransaction(response.data);
        console.log(response.data);
      }
    } catch (err) {
      console.error('Error fetching transaction details:', err);
    } finally {
      setModalLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTransactions(1, search, email);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchTransactions(page, search, email);
  };

  const handleViewTransaction = async (id) => {
    await fetchTransactionDetails(id);
    setShowModal(true);
  };

  const handleColumnFilter = (key, value) => {
    // Handle column filtering if needed
  };

  const handleSort = (sortConfig) => {
    // Handle sorting if needed
  };

  const handleRowAction = (actionKey, row, index) => {
    // Handle row actions if needed
  };

  const handleRowSelect = (selectedRows) => {
    // Handle row selection if needed
  };

  const handlePageSizeChange = (newPageSize) => {
    // Handle page size change if needed
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedTransaction(null);
  };

  useEffect(() => {
    fetchTransactions(1, '', '');
  }, []);

  const columns = [
    { 
      key: 'user', 
      title: 'User', 
      render: (value, row) => (
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 rounded-full overflow-hidden">
            <img 
              src={row.user?.image} 
              alt={row.user?.name} 
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">{row.user?.name}</p>
            <p className="text-xs text-gray-500">{row.user?.email}</p>
          </div>
        </div>
      )
    },
    { 
      key: 'reference', 
      title: 'Reference', 
      render: (value, row) => (
        <div>
          <p className="text-sm font-medium text-gray-900">{row.reference_shorted}</p>
          <p className="text-xs text-gray-500">{row.reference}</p>
        </div>
      )
    },
    { 
      key: 'total', 
      title: 'Amount', 
      render: (value, row) => (
        <div className="text-right">
          <p className="text-sm font-medium text-gray-900">
            {row.total} {row.currency_code}
          </p>
        </div>
      )
    },
    { 
      key: 'status', 
      title: 'Status', 
      render: (value, row) => {
        const statusColors = {
          pending: 'bg-yellow-100 text-yellow-800',
          completed: 'bg-green-100 text-green-800',
          failed: 'bg-red-100 text-red-800',
          cancelled: 'bg-gray-100 text-gray-800'
        };
        
        return (
          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${statusColors[row.status] || 'bg-gray-100 text-gray-800'}`}>
            {row.status}
          </span>
        );
      }
    },
    { 
      key: 'type', 
      title: 'Type', 
      render: (value, row) => (
        <span className="text-sm text-gray-900">{row.type_label}</span>
      )
    },
    { 
      key: 'created_at', 
      title: 'Date', 
      render: (value, row) => (
        <span className="text-sm text-gray-900">{row.created_at}</span>
      )
    },
    { 
      key: 'actions', 
      title: 'Actions', 
      render: (value, row) => (
        <button
          onClick={() => handleViewTransaction(row.id)}
          className="text-primary-600 hover:text-primary-900 p-1 rounded-full hover:bg-primary-50"
          title="View Details"
        >
          <FiEye className="h-4 w-4" />
        </button>
      )
    },
  ];

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <PageHeader title="Transactions" description="View and manage transaction records" user={user} />
        
        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                type="text"
                placeholder="Search by reference or user name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="flex-1">
              <Input
                type="email"
                placeholder="Filter by email..."
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full"
              />
            </div>
            <button
              type="submit"
              className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors duration-200 flex items-center justify-center space-x-2"
            >
              <FiSearch className="h-4 w-4" />
              <span>Search</span>
            </button>
          </form>
        </div>

        {/* Transactions Table */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Transactions List</h2>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-red-600">{error}</p>
              </div>
            ) : (
              <DataTable
                data={transactions}
                columns={columns}
                loading={loading}
                emptyMessage="No transactions found"
                pagination={{ 
                  current: currentPage, 
                  total: total, 
                  pageSize: limit, 
                  totalPages: totalPages 
                }}
                onSort={handleSort}
                onFilter={handleColumnFilter}
                onRowAction={handleRowAction}
                onRowSelect={handleRowSelect}
                onPageSizeChange={handlePageSizeChange}
                onPageChange={handlePageChange}
              />
            )}
          </div>
        </div>

        {/* Transaction Details Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>

              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                        Transaction Details
                      </h3>
                      
                      {modalLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                        </div>
                      ) : selectedTransaction ? (
                        <div className="space-y-4">
                          {/* User Info */}
                          <div className="border-b pb-4">
                            <h4 className="text-sm font-medium text-gray-500 mb-2">User Information</h4>
                            <div className="flex items-center space-x-3">
                              <div className="w-12 h-12 rounded-full overflow-hidden">
                                <img 
                                  src={selectedTransaction.user?.image} 
                                  alt={selectedTransaction.user?.name} 
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{selectedTransaction.user?.name}</p>
                                <p className="text-xs text-gray-500">{selectedTransaction.user?.email}</p>
                              </div>
                            </div>
                          </div>

                          {/* Transaction Details */}
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs text-gray-500">Reference</p>
                              <p className="text-sm font-medium text-gray-900">{selectedTransaction.reference_shorted}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Full Reference</p>
                              <p className="text-sm font-medium text-gray-900 break-all">{selectedTransaction.reference}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Amount</p>
                              <p className="text-sm font-medium text-gray-900">
                                {selectedTransaction.total} {selectedTransaction.currency_code}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Status</p>
                              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                selectedTransaction.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                selectedTransaction.status === 'completed' ? 'bg-green-100 text-green-800' :
                                selectedTransaction.status === 'failed' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {selectedTransaction.status}
                              </span>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Type</p>
                              <p className="text-sm font-medium text-gray-900">{selectedTransaction.type_label}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Date</p>
                              <p className="text-sm font-medium text-gray-900">{selectedTransaction.created_at}</p>
                            </div>
                          </div>

                                                     {/* Subscription Details if available */}
                           {selectedTransaction.subscription && (
                             <div className="border-t pt-4">
                               <h4 className="text-sm font-medium text-gray-500 mb-2">Subscription Details</h4>
                               <div className="grid grid-cols-2 gap-4">
                                 <div>
                                   <p className="text-xs text-gray-500">Name</p>
                                   <p className="text-sm font-medium text-gray-900">
                                     {selectedTransaction.subscription.name || 'N/A'}
                                   </p>
                                 </div>
                                 <div>
                                   <p className="text-xs text-gray-500">Profile Views/Month</p>
                                   <p className="text-sm font-medium text-gray-900">
                                     {selectedTransaction.subscription.profile_month_views || 'N/A'}
                                   </p>
                                 </div>
                                 <div>
                                   <p className="text-xs text-gray-500">Events/Month</p>
                                   <p className="text-sm font-medium text-gray-900">
                                     {selectedTransaction.subscription.events_per_month || 'N/A'}
                                   </p>
                                 </div>
                                 <div>
                                   <p className="text-xs text-gray-500">Start Date</p>
                                   <p className="text-sm font-medium text-gray-900">
                                     {selectedTransaction.subscription.start_date ? 
                                       new Date(selectedTransaction.subscription.start_date).toLocaleDateString() : 'N/A'
                                     }
                                   </p>
                                 </div>
                                 <div>
                                   <p className="text-xs text-gray-500">End Date</p>
                                   <p className="text-sm font-medium text-gray-900">
                                     {selectedTransaction.subscription.end_date ? 
                                       new Date(selectedTransaction.subscription.end_date).toLocaleDateString() : 'N/A'
                                     }
                                   </p>
                                 </div>
                               </div>
                             </div>
                           )}

                          {/* Failed Details if available */}
                          {selectedTransaction.failed_code && (
                            <div className="border-t pt-4">
                              <h4 className="text-sm font-medium text-gray-500 mb-2">Failure Details</h4>
                              <div className="space-y-2">
                                <div>
                                  <p className="text-xs text-gray-500">Error Code</p>
                                  <p className="text-sm font-medium text-gray-900">{selectedTransaction.failed_code}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500">Error Message</p>
                                  <p className="text-sm font-medium text-gray-900">{selectedTransaction.failed_message}</p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Transactions;
